import { FC, useState, useEffect, useRef } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import {useIntl} from 'react-intl'

import clipboardCopy from 'clipboard-copy';
import toastr from 'toastr';

import {MarkdownWithLinks} from '../../../components/MarkdownWithLinks'

import { ChatMessageModel } from '../../../models/chat';
import {Modal} from '../../../components/Modal'

import {MessageGenerating} from '../../../components/MessageGenerating'

import { chatMessageAPI } from '../../../api'

import { useAuth } from '../../../context/auth'

import {parseAttributedResponse, removeAttirubtionTags, decodeUnicode, removeThinkingPart} from '../../../utils'

import {mark_differences} from "../../../components/Diff";  

import {ChatResponseFeedbackModal} from './ChatResponseFeedbackModal'


interface Props {
  chatMessageResponse: ChatMessageModel
  show: boolean
  handleClose: () => void
}

const ChatResponseAttributionModal: FC<Props> = ({ chatMessageResponse, show, handleClose }) => {
  
  const intl = useIntl()
  
  const {currentUser} = useAuth()
  const currentSettings = currentUser?.settings

  const [attributionId, setAttributionId] = useState<string | undefined>()
  const [attributedResponse, setAttributedResponse] = useState<string | undefined>()
  const [attributedResult, setAttributedResult] = useState<any>()
  const [isAttributing, setIsAttributing] = useState<boolean>(true)
  const [isFailed, setIsFailed] = useState<boolean>(false)
  const [isTimedout, setIsTimedout] = useState<boolean>(false)
  const [notRequired, setNotRequired] = useState<boolean>(false)
  const [noRetrievedEvidences, setNoRetrievedEvidences] = useState<boolean>(false)
  const [showRevisions, setShowRevisions] = useState<boolean>(false)
  const [handlingReaction, setHandlingReaction] = useState<boolean>(false)
  const [showFeedbackModal, setShowFeedbackModal] = useState<boolean>(false)
  const [attributionReaction, setAttributionReaction] = useState<string | undefined>()

  const attributionResultRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if ( show ) {
      getAttributionResult()
    }
    return () => {
      if (attributionResultRef.current) {
        clearInterval(attributionResultRef.current)
      }
    };
  }, [show])

  const getAttributionResult = async () => {
    if( show && chatMessageResponse.id ){
      setIsFailed(false)
      setIsTimedout(false)
      setNotRequired(false)
      setNoRetrievedEvidences(false)
      setIsAttributing(true)
      chatMessageAPI.attribute(
        chatMessageResponse.chat_id, 
        chatMessageResponse.id
      ).then((response) => {
        setAttributionResult(response['data']['data'])
      }).catch((error) => {
        setIsFailed(true)
      })
    }
  }

  const setAttributionResult = (result: any) => {
    setAttributionId(result['attribution_id'])
    if( ['failed','timeout'].indexOf(result['status'].toLowerCase()) > -1  ){
      setIsFailed(true)
      setIsTimedout(result['status'].toLowerCase() === 'timeout')
      setIsAttributing(false)
    }else if( ['submitted','running'].indexOf(result['status'].toLowerCase()) > -1 ){
      attributionResultRef.current = setTimeout(getAttributionResult, 1000);
    }else if(result['status'].toLowerCase() === 'completed' ){
      setAttributedResponse(parseAttributedResponse(removeAttirubtionTags(result['attribution']['revised_response']), result['attribution']['references']))
      setAttributedResult(result['attribution'])
      setAttributionReaction(result['reaction_to_attribution'])
      setIsAttributing(false)
    }else if (result['status'].toLowerCase() === 'not_required' ){
      setNotRequired(true)
      setIsAttributing(false)
    }else if (result['status'].toLowerCase() === 'no_retrieved_evidences'){
      setNoRetrievedEvidences(true)
      setIsAttributing(false)
    }
  }


  const handleReaction = async (reaction: string) => {
    if( chatMessageResponse.id && chatMessageResponse && !handlingReaction ){
      setHandlingReaction(true)
      setAttributionReaction(attributionReaction == reaction ? undefined : reaction)
      if( attributionReaction != reaction && reaction === 'dislike' ){
        setShowFeedbackModal(true)
      }
      chatMessageAPI.react(chatMessageResponse.chat_id, chatMessageResponse.id, reaction, attributionId).then( () => {
        setHandlingReaction(false)
      })
    }
  }

  const handleCopy = () => {
    const uniqueReferences = Array.from(
      new Map(
        attributedResult['references'].map((reference: any) => [reference['number'], reference])
      ).values()
    ).sort((a: any, b: any) => a['number'] - b['number']);

	  clipboardCopy(
      decodeUnicode(removeAttirubtionTags(attributedResult['revised_response']))
      +
      '\n\n'
      +
      uniqueReferences.map((reference: any) => `[${reference['number']}] ${reference['url']}`).join('\n')
    );
	  toastr.info(intl.formatMessage({id: `chat.message.copied`}))
  };

  return <Modal
      dialogClassName='mw-1000px'
      show={show}
      handleClose={handleClose}
      title={intl.formatMessage({id: 'chat.message.attribute.title'})}
    >
      <input type="hidden" value=""/>
      <div className="row">
        <div className="col-lg-6 col-12 d-flex flex-column">
          <div className="d-flex flex-row px-0 rounded fs-4">
          <div className="d-flex flex-column">
            {intl.formatMessage({id: 'chat.message.attribute.before'})}
          </div>
        </div>
        <div className="mt-1 lh-lg mh-450px overflow-y-auto">
          <MarkdownWithLinks>{decodeUnicode(removeThinkingPart(chatMessageResponse.message))}</MarkdownWithLinks>
        </div>
      </div>
      <div className="col-lg-6 col-12 d-flex flex-column">
        <div className="d-flex flex-row px-0 rounded fs-4">
          <div className="d-flex flex-row align-items-center" >
            {intl.formatMessage({id: 'chat.message.attribute.after'})}
          </div>
        </div>
        <div className="mt-1 lh-lg mh-450px overflow-y-auto">
          { isFailed 
          ?
            intl.formatMessage({id: `chat.message.attribute.${isTimedout? 'timedout' : 'failed'}`})
          :
          isAttributing 
          ?
              <MessageGenerating text={intl.formatMessage({id: 'chat.message.attribute.processing'})}/>
          : 
            (
              showRevisions ?
                <MarkdownWithLinks>{parseAttributedResponse(mark_differences(decodeUnicode(removeThinkingPart(chatMessageResponse.message)), decodeUnicode(removeAttirubtionTags(attributedResult.revised_response))), attributedResult.references)}</MarkdownWithLinks>
                : 
                <MarkdownWithLinks>{decodeUnicode(attributedResponse)}</MarkdownWithLinks>
            )
          }
          { notRequired && <div className="text-muted">{intl.formatMessage({id: 'chat.message.attribute.not_required'})}</div>}
          { ( noRetrievedEvidences || (!isAttributing && attributedResult && attributedResult['references'] && attributedResult['references'].length == 0) ) && (
            <div className="text-danger opacity-50 mt-n5">{intl.formatMessage({id: 'chat.message.attribute.no_documents'})}</div>
          )}
        </div>
        <div className="mt-5 me-3 text-end">
          { !isAttributing && attributedResponse && attributedResult['references'] && attributedResult['references'].length > 0 &&
              <>
              <OverlayTrigger
                placement='top'
                overlay={<Tooltip>{intl.formatMessage({id: 'chat.message.copy'})}</Tooltip>}
              >
                <span className="cursor-pointer text-hover-dark py-1" onClick={handleCopy}>
                  <i className="fa-solid fa-copy fs-5"></i>
                </span>
              </OverlayTrigger>
              {currentSettings?.response_setting?.reaction &&
              <>
                <OverlayTrigger
                  placement='top'
                  overlay={<Tooltip>{intl.formatMessage({id: 'chat.message.attribute.good'})}</Tooltip>}
                >
                  <span className={`cursor-pointer ms-4 py-1`} onClick={()=>handleReaction('like')}>
                    <i className={`fa-solid fa-thumbs-up fs-5 ${attributionReaction && attributionReaction == 'like' ? 'text-primary' : 'text-hover-dark'}`}></i>
                  </span>
                </OverlayTrigger>
                <OverlayTrigger
                  placement='top'
                  overlay={<Tooltip>{intl.formatMessage({id: 'chat.message.attribute.bad'})}</Tooltip>}
                >
                  <span className={`cursor-pointer ms-4 py-1`} onClick={()=>handleReaction('dislike')}>
                    <i className={`fa-solid fa-thumbs-down fs-5 ${attributionReaction && attributionReaction == 'dislike' ? 'text-danger' : 'text-hover-dark'}`}></i>
                  </span>
                </OverlayTrigger>
              </>
              }
              <OverlayTrigger
                placement='top'
                overlay={<Tooltip>{intl.formatMessage({id: `chat.message.attribute.${showRevisions ? 'hide' : 'see' }_revisions`})}</Tooltip>}
              >
                <span className="cursor-pointer text-hover-dark py-1 ms-4" onClick={()=>setShowRevisions(!showRevisions)}>
                  <i className={`fa-solid fa-eye fs-5 ${showRevisions && 'text-dark'}`}></i>
                </span>
              </OverlayTrigger>
              </>
            }
        </div>
      </div>
    </div>
    { chatMessageResponse.id && attributionId &&(
      <ChatResponseFeedbackModal show={showFeedbackModal} handleClose={()=>setShowFeedbackModal(false)} type="attribution" chatId={chatMessageResponse.chat_id} chatMessageId={chatMessageResponse.id} attributionId={attributionId}/>
    )}
  </Modal>
}
  
export {ChatResponseAttributionModal}